import { useCallback, useEffect, useState } from 'react';

import { ErrorDto } from 'lib_api/lib/api/gen';

import {
  AlertMessage,
  useAlertMessage,
} from 'hooks/alertMessage/useAlertMessage';
import useDownloadFile from 'hooks/api/useDownloadFile';
import { useApi } from 'hooks/ApiStoreContext';
import { getErrorDtoOrThrow } from 'typeGuards/typeGuards';

import {
  HistoriqueAction,
  RecuperationActionsUtilisateurFormInProgressStatus,
  RecuperationActiviteUtilisateurFormValues,
} from '../types';
import { mapRecuperationActiviteUtilisateurFormValuesToRequest } from '../utils';

export interface UserSubmitRecuperationActionsUtilisateurFormReturnType {
  submitForm: (values: RecuperationActiviteUtilisateurFormValues) => void;
  inProgressActionStatus?: RecuperationActionsUtilisateurFormInProgressStatus;
  alertMessage?: AlertMessage;
  resetAlertMessage: () => void;
}

export const useSubmitRecuperationActionsUtilisateurForm = (
  selectedAction: HistoriqueAction | undefined,
): UserSubmitRecuperationActionsUtilisateurFormReturnType => {
  const [inProgressActionStatus, setInProgressActionStatus] = useState<
    RecuperationActionsUtilisateurFormInProgressStatus | undefined
  >(undefined);

  const {
    alertMessage,
    resetAlertMessage,
    setSuccessMessage,
    setErrorMessage,
  } = useAlertMessage();

  const catchErrorFor = (exportLabel: string) => (errorResponse: Response) => {
    return Promise.resolve(errorResponse)
      .then(response => response.json())
      .then(getErrorDtoOrThrow)
      .then((error: ErrorDto) => {
        setErrorMessage(
          `Erreur dans la génération de l’historique des ${exportLabel} : ${error.message}`,
        );
      })
      .catch(error => {
        console.error(
          'Erreur inattendue lors du traitement de l’erreur',
          error,
        );
        setErrorMessage(
          `Erreur inconnue lors de la génération de l’historique des ${exportLabel}`,
        );
      });
  };

  const controller = useApi().AdminFonctionnelControllerApi;

  const { mutateAsync: submitActions, isPending: inProgressActions } =
    useDownloadFile<RecuperationActiviteUtilisateurFormValues>(
      'historique_actions_utilisateur.csv',
      (values: RecuperationActiviteUtilisateurFormValues) =>
        controller.recupererHistoriqueActionsUtilisateurUsingPOST(
          mapRecuperationActiviteUtilisateurFormValuesToRequest(values),
        ),
      false,
      catchErrorFor('actions'),
      response =>
        Promise.resolve(response).then(() => {
          setSuccessMessage(
            'La génération de l’historique des actions est terminée avec succès',
          );
        }),
    );

  const { mutateAsync: submitConsultation, isPending: inProgressConsultation } =
    useDownloadFile<RecuperationActiviteUtilisateurFormValues>(
      'historique_consultations_dossier.csv',
      (values: RecuperationActiviteUtilisateurFormValues) =>
        controller.recupererHistoriqueConsultationsDossierUsingPOST(
          mapRecuperationActiviteUtilisateurFormValuesToRequest(values),
        ),
      false,
      catchErrorFor('consultations'),
      response =>
        Promise.resolve(response).then(() => {
          setSuccessMessage(
            'La génération de l’historique des consultations est terminée avec succès',
          );
        }),
    );

  const { mutateAsync: submitConnexions, isPending: inProgressConnexions } =
    useDownloadFile<RecuperationActiviteUtilisateurFormValues>(
      'historique_connexions_utilisateur.csv',
      (values: RecuperationActiviteUtilisateurFormValues) =>
        controller.recupererHistoriqueConnexionsUsingPOST(
          mapRecuperationActiviteUtilisateurFormValuesToRequest(values),
        ),
      false,
      catchErrorFor('connexions'),
      response =>
        Promise.resolve(response).then(() => {
          setSuccessMessage(
            'La génération de l’historique des connexions est terminée avec succès',
          );
        }),
    );

  const submitForm = useCallback(
    (values: RecuperationActiviteUtilisateurFormValues) => {
      switch (selectedAction) {
        case 'actions':
          return submitActions(values);
        case 'consultations':
          return submitConsultation(values);
        case 'connexions':
          return submitConnexions(values);
        default:
          return Promise.resolve();
      }
    },
    [selectedAction, submitActions, submitConsultation, submitConnexions],
  );

  useEffect(() => {
    setInProgressActionStatus({
      actions: {
        inProgress: inProgressActions,
      },
      consultations: {
        inProgress: inProgressConsultation,
      },
      connexions: {
        inProgress: inProgressConnexions,
      },
    });
  }, [inProgressActions, inProgressConsultation, inProgressConnexions]);

  return {
    submitForm,
    inProgressActionStatus,
    alertMessage,
    resetAlertMessage,
  };
};
